import { Fragment, useState } from 'react';
import { format, parseISO } from 'date-fns'; // Import date-fns for date formatting

import {
  Box,
  Typography,
  Button,
  TextField,
  Card,
  CardContent,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';

import api from 'utils/api';
import localData from 'utils/localData';

// constants
import { DOLPHIN_API } from 'shared/constants/apis';
import { USER_DATA } from 'shared/constants/users';

// components and parts
// import { PageLoader, PageError } from 'components';
import AttachmentViewer from './AttachmentViewer';
import logger from 'utils/logger';
// Alert severity values
const ALERT_SEVERITY = {
  SUCCESS: 'success',
  ERROR: 'error',
  WARNING: 'warning',
  INFO: 'info',
};

const propTypes = {};

const log = logger.createLogger('IWGDashboardParsedTextForIssues');

const IWGDashboardParsedTextForIssues = () => {
  const [issueIds, setIssueIds] = useState([]);
  const [currentIssueId, setCurrentIssueId] = useState('');
  const [issueResults, setIssueResults] = useState({});
  const [selectedAttachment, setSelectedAttachment] = useState(null);
  const [attachmentContent, setAttachmentContent] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [openMetadataDialog, setOpenMetadataDialog] = useState(false);
  const [selectedMetadata, setSelectedMetadata] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isAttachmentLoading, setIsAttachmentLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: ALERT_SEVERITY.SUCCESS,
  });

  const handleIssueIdChange = (event) => {
    setCurrentIssueId(event.target.value);
  };

  const addIssueId = async () => {
    if (!currentIssueId || currentIssueId.trim() === '') {
      setSnackbar({
        open: true,
        message: 'Please enter a valid issue ID',
        severity: ALERT_SEVERITY.WARNING,
      });
      return;
    }

    // Check if the issue ID is already in the list
    if (issueIds.includes(currentIssueId)) {
      setSnackbar({
        open: true,
        message: 'This issue ID is already in the list',
        severity: ALERT_SEVERITY.INFO,
      });
      return;
    }

    setIsLoading(true);
    try {
      const response = await api.get(
        `${DOLPHIN_API.NARWHAL_PARSED_TEXT_ISSUE_INFO}/${currentIssueId}`,
        {
          params: {
            username: localData.get(USER_DATA.USERNAME),
          },
        }
      );
      if (response) {
        // Add the issue ID to the list
        setIssueIds([...issueIds, currentIssueId]);

        // Store the issue result
        setIssueResults((prev) => ({
          ...prev,
          [currentIssueId]: response,
        }));

        // Clear the input
        setCurrentIssueId('');

        setSnackbar({
          open: true,
          message: `Issue ${currentIssueId} added successfully`,
          severity: ALERT_SEVERITY.SUCCESS,
        });
      }
    } catch (error) {
      console.error('Error fetching issue:', error);
      setSnackbar({
        open: true,
        message: `Failed to fetch issue ${currentIssueId}: ${
          error.message || 'Unknown error'
        }`,
        severity: ALERT_SEVERITY.ERROR,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const removeIssueId = (issueId) => {
    setIssueIds(issueIds.filter((id) => id !== issueId));

    // Also remove from results
    const updatedResults = { ...issueResults };
    delete updatedResults[issueId];
    setIssueResults(updatedResults);
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      addIssueId();
    }
  };

  const handleAttachmentClick = async (issueId, attachmentId) => {
    // Find the attachment info
    const issueData = issueResults[issueId] || [];
    const attachmentInfo = issueData.find(
      (item) => item.attachmentId === attachmentId
    );

    if (!attachmentInfo) {
      setSnackbar({
        open: true,
        message: 'Attachment information not found',
        severity: ALERT_SEVERITY.ERROR,
      });
      return;
    }

    setSelectedAttachment(attachmentInfo);
    setOpenDialog(true);
    setIsAttachmentLoading(true);

    try {
      // Fetch attachment content using the NARWHAL_PARSED_TEXT_ATTACHMENT endpoint
      const response = await api.get(
        `${DOLPHIN_API.NARWHAL_PARSED_TEXT_ATTACHMENT_CONTENT_ONLY}`,
        {
          params: {
            username: localData.get(USER_DATA.USERNAME),
            issueId: issueId,
            attachmentId: attachmentId,
          },
        }
      );

      if (response) {
        setAttachmentContent(response);
      }
    } catch (error) {
      console.error('Error fetching attachment:', error);
      setSnackbar({
        open: true,
        message: `Failed to fetch attachment: ${
          error.message || 'Unknown error'
        }`,
        severity: ALERT_SEVERITY.ERROR,
      });
      setAttachmentContent(null);
    } finally {
      setIsAttachmentLoading(false);
    }
  };

  const handleMetadataClick = (metadata, attachment) => {
    setSelectedMetadata({
      metadata,
      attachment,
    });
    setOpenMetadataDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedAttachment(null);
    setAttachmentContent(null);
  };

  const handleCloseMetadataDialog = () => {
    setOpenMetadataDialog(false);
    setSelectedMetadata(null);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';

    try {
      // Use date-fns to format the date with 24-hour format
      return format(parseISO(dateString), 'PPP HH:mm:ss');
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid date';
    }
  };

  // Helper function to render metadata in JSON format with formatted dates
  const renderFormattedJSON = (metadata) => {
    if (!metadata) return 'No metadata available';

    try {
      // Function to process metadata and format specific date fields
      const processMetadataWithDates = (data) => {
        // If it's not an object, return as is
        if (typeof data !== 'object' || data === null) {
          return data;
        }

        // Create a copy to avoid modifying the original
        const processed = { ...data };

        // Format specific date fields if they exist
        const dateFields = ['processed', 'creationDate', 'creationDate_s3'];

        dateFields.forEach((field) => {
          if (processed[field] !== undefined) {
            try {
              let formattedDate;

              // Check if it's a numeric timestamp (milliseconds since epoch)
              if (typeof processed[field] === 'number') {
                formattedDate = format(
                  new Date(processed[field]),
                  'PPP HH:mm:ss'
                );
                processed[field] = `${processed[field]} (${formattedDate})`;
              }
              // Check if it's a string date format
              else if (typeof processed[field] === 'string') {
                formattedDate = format(
                  parseISO(processed[field]),
                  'PPP HH:mm:ss'
                );
                processed[field] = `${processed[field]} (${formattedDate})`;
              }
            } catch (err) {
              // If parsing fails, leave the original value
              console.error(`Error formatting date field ${field}:`, err);
            }
          }
        });

        // Recursively process nested objects
        Object.keys(processed).forEach((key) => {
          if (typeof processed[key] === 'object' && processed[key] !== null) {
            processed[key] = processMetadataWithDates(processed[key]);
          }
        });

        return processed;
      };

      // If it's already an object, process it and stringify with pretty formatting
      if (typeof metadata === 'object') {
        const processedMetadata = processMetadataWithDates(metadata);
        return (
          <pre
            style={{
              backgroundColor: '#f5f5f5',
              padding: '16px',
              borderRadius: '4px',
              overflow: 'auto',
              maxHeight: '400px',
            }}>
            {JSON.stringify(processedMetadata, null, 2)}
          </pre>
        );
      }

      // If it's a string but contains JSON, parse, process, and format it
      try {
        const parsed = JSON.parse(metadata);
        const processedMetadata = processMetadataWithDates(parsed);
        return (
          <pre
            style={{
              backgroundColor: '#f5f5f5',
              padding: '16px',
              borderRadius: '4px',
              overflow: 'auto',
              maxHeight: '400px',
            }}>
            {JSON.stringify(processedMetadata, null, 2)}
          </pre>
        );
      } catch (e) {
        // If it can't be parsed as JSON, just show as is
        log.error('Error rendering JSON:', e);
        return metadata.toString();
      }
    } catch (error) {
      log.error('Error rendering JSON:', error);
      return 'Error displaying metadata';
    }
  };

  return (
    <Fragment>
      <Box sx={{ padding: '24px' }}>
        <Typography variant="h3" gutterBottom>
          Parsed Text for Issues
        </Typography>

        <Grid
          container
          spacing={2}
          alignItems="center"
          style={{ marginBottom: '20px' }}>
          <Grid size={8}>
            <TextField
              fullWidth
              label="Issue ID"
              name="issue_id"
              variant="outlined"
              value={currentIssueId}
              onChange={handleIssueIdChange}
              onKeyPress={handleKeyPress}
              disabled={isLoading}
              placeholder="Enter issue ID and press Enter or click Add"
            />
          </Grid>
          <Grid size={4}>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              onClick={addIssueId}
              disabled={isLoading}
              startIcon={
                isLoading ? <CircularProgress size={20} /> : <AddIcon />
              }>
              {isLoading ? 'Loading...' : 'Add Issue'}
            </Button>
          </Grid>
        </Grid>

        {issueIds.length > 0 && (
          <Box sx={{ marginBottom: '20px' }}>
            <Typography variant="h6" gutterBottom>
              Current Issues:
            </Typography>
            <Box display="flex" flexWrap="wrap" style={{ gap: '8px' }}>
              {issueIds.map((id) => (
                <Chip
                  key={id}
                  label={id}
                  onDelete={() => removeIssueId(id)}
                  color="primary"
                />
              ))}
            </Box>
          </Box>
        )}

        {issueIds.length > 0 ? (
          <Grid container spacing={3}>
            {issueIds.map((issueId) => {
              const issueData = issueResults[issueId] || [];

              return (
                <Grid size={12} key={issueId}>
                  <Card>
                    <CardContent>
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        mb={2}>
                        <Typography variant="h5">Issue #{issueId}</Typography>
                        <IconButton
                          size="small"
                          onClick={() => removeIssueId(issueId)}
                          aria-label="remove issue">
                          <CloseIcon />
                        </IconButton>
                      </Box>

                      <Typography variant="h6" gutterBottom>
                        Attachments ({issueData.length})
                      </Typography>

                      {issueData.length > 0 ? (
                        <TableContainer component={Paper}>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Attachment ID</TableCell>
                                <TableCell>File Name</TableCell>
                                <TableCell>File Type</TableCell>
                                <TableCell>Created</TableCell>
                                <TableCell>Actions</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {issueData.map((attachment) => (
                                <TableRow key={attachment.attachmentId}>
                                  <TableCell>
                                    {attachment.attachmentId}
                                  </TableCell>
                                  <TableCell>
                                    {attachment.filename || 'N/A'}
                                  </TableCell>
                                  <TableCell>
                                    {attachment.filetype || 'N/A'}
                                  </TableCell>
                                  <TableCell>
                                    {formatDate(attachment.createdAt)}
                                  </TableCell>
                                  <TableCell>
                                    <Box display="flex" style={{ gap: '8px' }}>
                                      <Button
                                        variant="contained"
                                        color="primary"
                                        style={{ color: '#fff' }}
                                        size="small"
                                        onClick={() =>
                                          handleAttachmentClick(
                                            issueId,
                                            attachment.attachmentId,
                                            attachment
                                          )
                                        }>
                                        View Content
                                      </Button>
                                      <Button
                                        variant="contained"
                                        color="primary"
                                        style={{ color: '#fff' }}
                                        size="small"
                                        onClick={() =>
                                          handleMetadataClick(
                                            attachment.metaData,
                                            attachment
                                          )
                                        }>
                                        Metadata
                                      </Button>
                                    </Box>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      ) : (
                        <Typography variant="body2" color="textSecondary">
                          No attachments available
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        ) : (
          <Box sx={{ textAlign: 'center', padding: '40px 0' }}>
            <Typography variant="h6" color="textSecondary">
              No issues added yet. Enter an issue ID above to start.
            </Typography>
          </Box>
        )}
      </Box>

      {/* Attachment Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="xl"
        fullWidth
        aria-labelledby="attachment-dialog-title">
        <DialogTitle id="attachment-dialog-title">
          {selectedAttachment?.fileName || 'Attachment Viewer'}
        </DialogTitle>
        <DialogContent dividers>
          {isAttachmentLoading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : (
            selectedAttachment && (
              <AttachmentViewer
                attachmentData={attachmentContent}
                selectedAttachment={selectedAttachment}
              />
            )
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Metadata Dialog */}
      <Dialog
        open={openMetadataDialog}
        onClose={handleCloseMetadataDialog}
        maxWidth="xl"
        fullWidth
        aria-labelledby="metadata-dialog-title">
        <DialogTitle id="metadata-dialog-title">
          Metadata:{' '}
          {selectedMetadata?.attachment?.filename || 'Attachment Metadata'}
        </DialogTitle>
        <DialogContent dividers>
          {selectedMetadata ? (
            <Box>
              <Grid container spacing={2} style={{ marginBottom: '16px' }}>
                <Grid size={6}>
                  <Typography variant="body2">
                    <strong>Attachment ID:</strong>{' '}
                    {selectedMetadata.attachment.attachmentId || 'N/A'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>File Name:</strong>{' '}
                    {selectedMetadata.attachment.filename || 'N/A'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>File Type:</strong>{' '}
                    {selectedMetadata.attachment.filetype || 'N/A'}
                  </Typography>
                </Grid>
                <Grid size={6}>
                  <Typography variant="body2">
                    <strong>Created:</strong>{' '}
                    {formatDate(selectedMetadata.attachment.createdAt)}
                  </Typography>
                  <Typography variant="body2">
                    <strong>File Path:</strong>{' '}
                    {selectedMetadata.attachment.filepath || 'N/A'}
                  </Typography>
                </Grid>
              </Grid>

              <Typography variant="h6" gutterBottom>
                Detailed Metadata
              </Typography>
              {renderFormattedJSON(selectedMetadata.metadata)}
            </Box>
          ) : (
            <Typography>No metadata available</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseMetadataDialog} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}>
        <Alert
          onClose={handleCloseSnackbar}
          severity={
            snackbar.severity === ALERT_SEVERITY.SUCCESS
              ? 'success'
              : snackbar.severity === ALERT_SEVERITY.ERROR
              ? 'error'
              : snackbar.severity === ALERT_SEVERITY.WARNING
              ? 'warning'
              : 'info'
          }>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Fragment>
  );
};

IWGDashboardParsedTextForIssues.propTypes = propTypes;

export default IWGDashboardParsedTextForIssues;
